// src/components/kuestion/pages/QuestionBuilder.jsx
//
// Write questions by hand, for users with no AI access — or anyone who just
// wants a short bank without a round trip through a chat window.
//
// Deliberately needs no new backend: the finished bank is serialised to a Blob,
// wrapped in a File, and posted to the existing multipart POST /data/upload.
// The server validates it exactly as it would an LLM-produced file.
import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import './QuestionBuilder.css';

const LETTERS = 'ABCDEFGHIJ'.split('');

const PlusIcon = () => (<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>);
const TrashIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h12M8 6V4h4v2m-7 0v10a1 1 0 001 1h6a1 1 0 001-1V6"/></svg>);
const CloseIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/></svg>);
const DownloadIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3v9m-4-4l4 4 4-4"/><path d="M3 15v1a1 1 0 001 1h12a1 1 0 001-1v-1"/></svg>);
const UploadIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14V3m-4 4l4-4 4 4"/><path d="M3 14v2a1 1 0 001 1h12a1 1 0 001-1v-2"/></svg>);

const blankQuestion = () => ({
  key: Math.random().toString(36).slice(2),
  question: '',
  type: 'multichoice',
  options: ['', '', '', ''],
  correctIndex: null,
  explanation: '',
});

const QuestionBuilder = ({ onClose, onUpload }) => {
  const [meta, setMeta] = useState({ examCode: '', module: '', type: 'multichoice' });
  const [questions, setQuestions] = useState([blankQuestion()]);
  const [openIndex, setOpenIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const patchQuestion = (i, patch) =>
    setQuestions(qs => qs.map((q, n) => (n === i ? { ...q, ...patch } : q)));

  const addQuestion = () => {
    setQuestions(qs => [...qs, blankQuestion()]);
    setOpenIndex(questions.length);
  };

  const removeQuestion = (i) => {
    setQuestions(qs => qs.filter((_, n) => n !== i));
    setOpenIndex(n => (n >= i && n > 0 ? n - 1 : n));
  };

  const setOption = (qi, oi, value) =>
    setQuestions(qs => qs.map((q, n) => (n === qi
      ? { ...q, options: q.options.map((o, m) => (m === oi ? value : o)) }
      : q)));

  const addOption = (qi) =>
    setQuestions(qs => qs.map((q, n) => (n === qi && q.options.length < LETTERS.length
      ? { ...q, options: [...q.options, ''] }
      : q)));

  // Removing an option shifts every later letter, so the stored correct index
  // has to move with it or the answer silently points at the wrong option.
  const removeOption = (qi, oi) =>
    setQuestions(qs => qs.map((q, n) => {
      if (n !== qi || q.options.length <= 2) return q;
      const options = q.options.filter((_, m) => m !== oi);
      let correctIndex = q.correctIndex;
      if (correctIndex === oi) correctIndex = null;
      else if (correctIndex != null && correctIndex > oi) correctIndex -= 1;
      return { ...q, options, correctIndex };
    }));

  // Same rules the uploader and the server enforce, checked live so the user
  // can't build something that will be rejected on arrival.
  const problems = useMemo(() => {
    const list = [];
    if (!meta.examCode.trim()) list.push({ q: null, msg: 'Exam code is required.' });
    if (!meta.module.trim()) list.push({ q: null, msg: 'Module is required.' });
    if (!questions.length) list.push({ q: null, msg: 'Add at least one question.' });

    questions.forEach((q, i) => {
      const n = i + 1;
      if (!q.question.trim()) list.push({ q: i, msg: `Q${n}: question text is empty.` });
      const filled = q.options.filter(o => o.trim());
      if (filled.length < 2) list.push({ q: i, msg: `Q${n}: needs at least 2 non-empty options.` });
      if (q.options.some(o => !o.trim())) list.push({ q: i, msg: `Q${n}: remove or fill the blank options.` });
      if (q.correctIndex == null) list.push({ q: i, msg: `Q${n}: mark which option is correct.` });
    });
    return list;
  }, [meta, questions]);

  const invalidQuestions = useMemo(
    () => new Set(problems.filter(p => p.q != null).map(p => p.q)),
    [problems],
  );

  const isValid = problems.length === 0;
  const fileName = `${(meta.examCode || 'questions').trim().replace(/\s+/g, '_')}.json`;

  // Emits the agreed §10 shape, including the "A. " option prefix the exam
  // runner still parses. When Q6 lands this is the one place to change.
  const buildJson = () => ({
    examCode: meta.examCode.trim(),
    module: meta.module.trim(),
    type: meta.type,
    rewordLevel: 'normal',
    totalQuestions: questions.length,
    questions: questions.map((q, i) => ({
      id: i + 1,
      question: q.question.trim(),
      type: meta.type === 'mixed' ? q.type : meta.type,
      options: q.options.map((o, oi) => `${LETTERS[oi]}. ${o.trim()}`),
      correctAnswer: LETTERS[q.correctIndex],
      explanation: q.explanation.trim(),
    })),
  });

  const download = () => {
    const blob = new Blob([JSON.stringify(buildJson(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    setBusy(true);
    setError('');
    try {
      const blob = new Blob([JSON.stringify(buildJson(), null, 2)], { type: 'application/json' });
      await onUpload(new File([blob], fileName, { type: 'application/json' }));
      onClose();
    } catch (e) {
      setError(e.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="qb-overlay" onClick={() => !busy && onClose()}>
      <div className="qb-modal" onClick={e => e.stopPropagation()}>

        <header className="qb-header">
          <div>
            <h2>Create questions manually</h2>
            <p>Write your own bank — no AI needed. It is validated as you go and uploaded in the same format.</p>
          </div>
          <button className="qb-close" onClick={onClose} disabled={busy} aria-label="Close"><CloseIcon /></button>
        </header>

        <div className="qb-body">
          <div className="qb-meta">
            <div className="qb-field">
              <label htmlFor="qb-code">Exam Code <span className="qb-req">*</span></label>
              <input id="qb-code" type="text" placeholder="e.g. CISSP-Ch01" value={meta.examCode}
                onChange={e => setMeta(m => ({ ...m, examCode: e.target.value }))} />
            </div>
            <div className="qb-field">
              <label htmlFor="qb-module">Module <span className="qb-req">*</span></label>
              <input id="qb-module" type="text" placeholder="e.g. Security Governance" value={meta.module}
                onChange={e => setMeta(m => ({ ...m, module: e.target.value }))} />
            </div>
            <div className="qb-field">
              <label htmlFor="qb-type">Question Type</label>
              <select id="qb-type" value={meta.type} onChange={e => setMeta(m => ({ ...m, type: e.target.value }))}>
                <option value="multichoice">Multiple Choice</option>
                <option value="draganddrop">Drag &amp; Drop</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
          </div>

          <div className="qb-list">
            {questions.map((q, i) => {
              const open = openIndex === i;
              const bad = invalidQuestions.has(i);
              return (
                <section key={q.key} className={`qb-card${open ? ' qb-open' : ''}${bad ? ' qb-bad' : ''}`}>
                  <button className="qb-card-head" onClick={() => setOpenIndex(open ? -1 : i)} aria-expanded={open}>
                    <span className="qb-num">{i + 1}</span>
                    <span className="qb-summary">{q.question.trim() || <em>Untitled question</em>}</span>
                    {bad && <span className="qb-flag">Incomplete</span>}
                  </button>

                  {open && (
                    <div className="qb-card-body">
                      <div className="qb-field">
                        <label htmlFor={`qb-q-${q.key}`}>Question</label>
                        <textarea id={`qb-q-${q.key}`} rows={3} placeholder="What are you asking?"
                          value={q.question} onChange={e => patchQuestion(i, { question: e.target.value })} />
                      </div>

                      {meta.type === 'mixed' && (
                        <div className="qb-field">
                          <label htmlFor={`qb-t-${q.key}`}>This question is</label>
                          <select id={`qb-t-${q.key}`} value={q.type} onChange={e => patchQuestion(i, { type: e.target.value })}>
                            <option value="multichoice">Multiple Choice</option>
                            <option value="draganddrop">Drag &amp; Drop</option>
                          </select>
                        </div>
                      )}

                      <div className="qb-field">
                        <label>Options — select the correct one</label>
                        <div className="qb-options">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className={`qb-option${q.correctIndex === oi ? ' qb-correct' : ''}`}>
                              <button
                                type="button"
                                className="qb-mark"
                                onClick={() => patchQuestion(i, { correctIndex: oi })}
                                title={q.correctIndex === oi ? 'This is the correct answer' : 'Mark as correct'}
                                aria-pressed={q.correctIndex === oi}
                              >
                                {LETTERS[oi]}
                              </button>
                              <input type="text" placeholder={`Option ${LETTERS[oi]}`} value={opt}
                                onChange={e => setOption(i, oi, e.target.value)} />
                              <button type="button" className="qb-drop" onClick={() => removeOption(i, oi)}
                                disabled={q.options.length <= 2} title={q.options.length <= 2 ? 'A question needs at least two options' : 'Remove option'}>
                                <CloseIcon />
                              </button>
                            </div>
                          ))}
                        </div>
                        {q.options.length < LETTERS.length && (
                          <button type="button" className="qb-add-option" onClick={() => addOption(i)}>
                            <PlusIcon /> Add option
                          </button>
                        )}
                      </div>

                      <div className="qb-field">
                        <label htmlFor={`qb-e-${q.key}`}>Explanation <span className="qb-opt">optional</span></label>
                        <input id={`qb-e-${q.key}`} type="text" placeholder="Shown on the results screen after the exam"
                          value={q.explanation} onChange={e => patchQuestion(i, { explanation: e.target.value })} />
                      </div>

                      <div className="qb-card-actions">
                        <button type="button" className="qb-remove" onClick={() => removeQuestion(i)} disabled={questions.length <= 1}>
                          <TrashIcon /> Remove question
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          <button type="button" className="qb-add" onClick={addQuestion}><PlusIcon /> Add question</button>

          {problems.length > 0 && (
            <div className="qb-problems">
              <strong>{problems.length} thing{problems.length === 1 ? '' : 's'} left to fix</strong>
              <ul>{problems.slice(0, 6).map((p, n) => <li key={n}>{p.msg}</li>)}</ul>
              {problems.length > 6 && <p className="qb-more">…and {problems.length - 6} more.</p>}
            </div>
          )}

          {error && <div className="qb-error">{error}</div>}
        </div>

        <footer className="qb-footer">
          <span className="qb-count">
            {questions.length} question{questions.length === 1 ? '' : 's'}
            {isValid && <span className="qb-ok"> · ready</span>}
          </span>
          <div className="qb-footer-actions">
            <button className="qb-btn-ghost" onClick={download} disabled={!isValid || busy}>
              <DownloadIcon /> Download .json
            </button>
            <button className="qb-btn-primary" onClick={upload} disabled={!isValid || busy}>
              <UploadIcon /> {busy ? 'Uploading…' : 'Save to Kuestion'}
            </button>
          </div>
        </footer>

      </div>
    </div>,
    document.body,
  );
};

export default QuestionBuilder;
